import { useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/Modal";
import LapForm from "./LapForm";
import { useUpdateLap } from "@/hooks/useLaps";
import type { Lap, UpdateLapRequest } from "@/types";

interface LapModalProps {
  isOpen: boolean;
  onClose: () => void;
  lap: Lap;
}

export default function LapModal({
  isOpen,
  onClose,
  lap,
}: LapModalProps) {
  const [error, setError] = useState("");
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const { t } = useTranslation(["pages", "common"]);

  const updateLap = useUpdateLap();

  const isLoading = updateLap.isPending;

  const handleSubmit = async (data: UpdateLapRequest) => {
    setError("");
    setServerErrors({});

    try {
      await updateLap.mutateAsync({
        sessionId: lap.sessionId,
        lapId: lap.id,
        ...data,
      });
      onClose();
    } catch (err: unknown) {
      const resp = (
        err as {
          response?: {
            data?: {
              error?: {
                details?: Array<{
                  path?: (string | number)[];
                  message: string;
                }>;
              };
              message?: string;
            };
          };
        }
      ).response;
      const fallback = t("pages:sessions.detail.laps.modal.failedToUpdate");

      if (resp?.data?.error?.details) {
        const details = resp.data.error.details as Array<{
          path?: (string | number)[];
          message: string;
        }>;
        const fieldErrors: Record<string, string> = {};
        details.forEach((d) => {
          const field = d.path?.[0] as string | undefined;
          if (field) {
            fieldErrors[field] = d.message;
          }
        });
        setServerErrors(fieldErrors);
        setError("");
      } else {
        setError(resp?.data?.message || fallback);
      }
    }
  };

  const handleClose = () => {
    setError("");
    setServerErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("pages:sessions.detail.laps.modal.editTitle")}
      size="lg"
    >
      {error && (
 <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <LapForm
        onSubmit={handleSubmit}
        onCancel={handleClose}
        initialData={lap}
        isLoading={isLoading}
        serverErrors={serverErrors}
      />
    </Modal>
  );
}
